import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Users, BookMarked, Upload, Sparkles, Check, ExternalLink,
  MessageSquare, Newspaper, ArrowRight, Wand2, Library, Layers, PlayCircle, Printer, Search,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import VRViewer from "@/components/VRViewer";
import { printLesson } from "@/lib/printLesson";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";

const SUBJECTS = ["Science", "History", "Math", "English Language Arts", "Geography", "Art"];
const SKOOL_URL = "https://www.skool.com/";

const STATUS = {
  pending_review: { label: "Awaiting approval", cls: "bg-amber-500/15 text-amber-700" },
  needs_manual_review: { label: "Needs manual review", cls: "bg-slate-500/15 text-slate-700" },
  approved: { label: "Approved · feeding generator", cls: "bg-emerald-500/15 text-emerald-700" },
};

export default function CohortDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [contribs, setContribs] = useState([]);
  const [library, setLibrary] = useState([]);
  const [allLessons, setAllLessons] = useState([]);
  const [launch, setLaunch] = useState(null);
  const [lessonSearch, setLessonSearch] = useState("");
  const [lessonSubject, setLessonSubject] = useState("all");
  const [lessonGrade, setLessonGrade] = useState("all");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", subject: "Science", grade: "", content: "", feedback: "" });
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState({}); // contribId -> Set of tips

  const load = async () => {
    try {
      const [c, l, al] = await Promise.all([
        api.get("/cohort/contributions"), api.get("/library"), api.get("/all-lessons"),
      ]);
      setContribs(c.data);
      setLibrary(l.data);
      setAllLessons(al.data || []);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (k) => (v) => setForm({ ...form, [k]: typeof v === "string" ? v : v.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) { toast.error("Add a title and lesson content"); return; }
    setSubmitting(true);
    try {
      await api.post("/cohort/contributions", form);
      toast.success("Uploaded! Review the suggested improvements below.");
      setForm({ title: "", subject: "Science", grade: "", content: "", feedback: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTip = (cid, tip) => {
    setSelected((prev) => {
      const set = new Set(prev[cid] ?? []);
      set.has(tip) ? set.delete(tip) : set.add(tip);
      return { ...prev, [cid]: set };
    });
  };
  const isChecked = (c, tip) => {
    const s = selected[c.id];
    return s ? s.has(tip) : true; // default all checked
  };

  const approve = async (c) => {
    const chosen = (c.suggestions || []).filter((t) => isChecked(c, t));
    try {
      await api.post(`/cohort/contributions/${c.id}/approve`, { approved_suggestions: chosen });
      toast.success("Approved — improvements now feed the generator & the lesson is in the shared library.");
      load();
    } catch (e) {
      toast.error("Approval failed");
    }
  };

  if (loading) {
    return (<div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></div>);
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Badge className="bg-accent/10 text-accent border-0 mb-2">Educator Cohort</Badge>
            <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight">Welcome, {user?.name?.split(" ")[0]} 👋</h1>
            <p className="text-muted-foreground mt-1">Build lessons, share feedback that improves the generator, and publish to the shared library.</p>
          </div>
          <Button data-testid="cohort-generator-btn" onClick={() => nav("/generator")} className="rounded-full h-11 px-6"><Wand2 className="w-4 h-4 mr-2" /> Open Generator</Button>
        </div>

        {/* Community links */}
        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <a href={SKOOL_URL} target="_blank" rel="noreferrer" data-testid="cohort-skool-community" className="rounded-2xl border border-border bg-card p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all group">
            <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Users className="w-5 h-5" /></div>
            <div className="font-display font-semibold mt-3 flex items-center gap-1">Skool Community <ExternalLink className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100" /></div>
            <p className="text-sm text-muted-foreground mt-1">Working groups & discussions with the founding cohort.</p>
          </a>
          <a href={SKOOL_URL} target="_blank" rel="noreferrer" data-testid="cohort-skool-blog" className="rounded-2xl border border-border bg-card p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all group">
            <div className="w-11 h-11 rounded-xl bg-accent/10 text-accent flex items-center justify-center"><Newspaper className="w-5 h-5" /></div>
            <div className="font-display font-semibold mt-3 flex items-center gap-1">Community Blog <ExternalLink className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100" /></div>
            <p className="text-sm text-muted-foreground mt-1">Playbooks, prompting guides & release notes.</p>
          </a>
          <a href={SKOOL_URL} target="_blank" rel="noreferrer" data-testid="cohort-skool-discuss" className="rounded-2xl border border-border bg-card p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all group">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center"><MessageSquare className="w-5 h-5" /></div>
            <div className="font-display font-semibold mt-3 flex items-center gap-1">Weekly Sync Threads <ExternalLink className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100" /></div>
            <p className="text-sm text-muted-foreground mt-1">Async feedback that shapes the roadmap.</p>
          </a>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          {/* Upload form */}
          <div className="rounded-3xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-4"><Upload className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Upload a lesson with feedback</h2></div>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Lesson title</Label><Input data-testid="cohort-title-input" value={form.title} onChange={setField("title")} className="mt-1.5 h-11" placeholder="e.g. Cell Structure Deep Dive" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Subject</Label>
                  <Select value={form.subject} onValueChange={setField("subject")}>
                    <SelectTrigger data-testid="cohort-subject-select" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Grade</Label><Input data-testid="cohort-grade-input" value={form.grade} onChange={setField("grade")} className="mt-1.5 h-11" placeholder="e.g. 7th Grade" /></div>
              </div>
              <div><Label>Lesson content / outline</Label><Textarea data-testid="cohort-content-input" value={form.content} onChange={setField("content")} className="mt-1.5 min-h-28" placeholder="Paste the lesson outline, script, or scene description you built…" /></div>
              <div><Label>What worked / what to improve</Label><Textarea data-testid="cohort-feedback-input" value={form.feedback} onChange={setField("feedback")} className="mt-1.5 min-h-20" placeholder="Your feedback and goals — the AI turns this into reusable improvements." /></div>
              <Button type="submit" data-testid="cohort-submit-btn" disabled={submitting} className="w-full h-11 rounded-full">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Upload & get AI suggestions</>}
              </Button>
            </form>
          </div>

          {/* Contributions */}
          <div className="rounded-3xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-4"><BookMarked className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Your contributions</h2></div>
            <div className="space-y-4 max-h-[560px] overflow-auto">
              {contribs.length === 0 && <p className="text-sm text-muted-foreground">Upload a lesson to see AI-suggested improvements you can approve.</p>}
              {contribs.map((c) => {
                const st = STATUS[c.status] || STATUS.pending_review;
                return (
                  <div key={c.id} data-testid={`contribution-${c.id}`} className="rounded-2xl border border-border p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div><div className="font-semibold text-sm">{c.title}</div><div className="text-xs text-muted-foreground">{c.subject}{c.grade ? ` · ${c.grade}` : ""}</div></div>
                      <Badge className={`${st.cls} border-0 text-[10px]`}>{st.label}</Badge>
                    </div>
                    {c.status === "approved" ? (
                      <div className="mt-3 text-sm">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 mb-1">Approved improvements</div>
                        <ul className="space-y-1">{(c.approved_suggestions || []).map((t, i) => <li key={i} className="flex gap-2 text-sm text-muted-foreground"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {t}</li>)}</ul>
                      </div>
                    ) : (c.suggestions || []).length > 0 ? (
                      <div className="mt-3">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Suggested improvements — pick which to approve</div>
                        <div className="space-y-2">
                          {c.suggestions.map((t, i) => (
                            <label key={i} className="flex items-start gap-2 text-sm cursor-pointer">
                              <input type="checkbox" data-testid={`contribution-tip-${c.id}-${i}`} checked={isChecked(c, t)} onChange={() => toggleTip(c.id, t)} className="mt-1 accent-[hsl(244_68%_61%)]" />
                              <span>{t}</span>
                            </label>
                          ))}
                        </div>
                        <Button data-testid={`contribution-approve-${c.id}`} onClick={() => approve(c)} className="mt-3 w-full rounded-full h-9 bg-emerald-500 hover:bg-emerald-600 text-white">
                          <Check className="w-4 h-4 mr-1" /> Approve & feed generator
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-2">No AI suggestions generated. You can still approve to publish to the library.</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* All lessons across teachers */}
        <div className="rounded-3xl border border-border bg-card p-6 mt-6" data-testid="cohort-all-lessons">
          {(() => {
            const subjects = Array.from(new Set(allLessons.map((l) => l.subject).filter(Boolean))).sort();
            const grades = Array.from(new Set(allLessons.map((l) => l.grade).filter(Boolean))).sort();
            const q = lessonSearch.trim().toLowerCase();
            const filtered = allLessons.filter((l) =>
              (lessonSubject === "all" || l.subject === lessonSubject) &&
              (lessonGrade === "all" || l.grade === lessonGrade) &&
              (!q || [l.title, l.subject, l.grade, l.state, l.topic, l.teacher_name].some((v) => (v || "").toLowerCase().includes(q)))
            );
            return (<>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2"><Layers className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">All lessons <span className="text-sm font-normal text-muted-foreground">· {filtered.length} of {allLessons.length}</span></h2></div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input data-testid="cohort-lesson-search-input" value={lessonSearch} onChange={(e) => setLessonSearch(e.target.value)} placeholder="Search lessons…" className="h-9 w-56 pl-9" />
                  </div>
                  <Select value={lessonSubject} onValueChange={setLessonSubject}>
                    <SelectTrigger data-testid="cohort-lesson-subject-filter" className="h-9 w-36"><SelectValue placeholder="Subject" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All subjects</SelectItem>{subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={lessonGrade} onValueChange={setLessonGrade}>
                    <SelectTrigger data-testid="cohort-lesson-grade-filter" className="h-9 w-32"><SelectValue placeholder="Grade" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All grades</SelectItem>{grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {filtered.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">{allLessons.length === 0 ? "No lessons have been built yet." : "No lessons match your filters."}</p> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map((l) => (
                    <div key={l.id} data-testid={`cohort-lesson-${l.id}`} className="rounded-xl border border-border p-4 flex flex-col">
                      <div className="font-semibold text-sm">{l.title}</div>
                      <div className="text-xs text-muted-foreground">{l.subject} · {l.grade} · {l.state}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">by {l.teacher_name}</div>
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {l.style && <Badge variant="secondary" className="text-[10px]">{l.style}</Badge>}
                        {l.library_status === "approved" && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-700 border-0">In library</Badge>}
                      </div>
                      <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                        <Button size="sm" variant="secondary" data-testid={`cohort-launch-lesson-${l.id}`} onClick={() => setLaunch(l)} className="flex-1 h-8"><PlayCircle className="w-4 h-4 mr-1" /> Launch</Button>
                        <Button size="icon" variant="ghost" data-testid={`cohort-print-lesson-${l.id}`} onClick={() => printLesson(l)} className="h-8 w-8"><Printer className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>);
          })()}
        </div>

        {/* Shared library (also visible on teacher dashboard) */}
        <div className="rounded-3xl border border-border bg-card p-6 mt-6" data-testid="cohort-library">
          <div className="flex items-center gap-2 mb-4"><Library className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-lg">Shared library <span className="text-sm font-normal text-muted-foreground">· published to all teachers</span></h2></div>
          {library.length === 0 ? <p className="text-sm text-muted-foreground">Approved lessons appear here and on every teacher's dashboard.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {library.map((l) => (
                <div key={l.id} className="rounded-xl border border-border p-4">
                  <div className="font-semibold text-sm">{l.title}</div>
                  <div className="text-xs text-muted-foreground">{l.subject}{l.grade ? ` · ${l.grade}` : ""} · by {l.author}</div>
                  <Badge variant="secondary" className="mt-2 text-[10px]">{(l.improvements || []).length} approved improvements</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Launch dialog */}
      <Dialog open={!!launch} onOpenChange={(o) => !o && setLaunch(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{launch?.title}</DialogTitle>
            <DialogDescription>{launch?.state} · {launch?.grade} · {launch?.subject} · {launch?.topic} · by {launch?.teacher_name}</DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl overflow-hidden border border-slate-800 bg-[#0B0F19] h-[440px]" data-testid="cohort-launch-vr-container">
            {launch && <VRViewer scene={launch.vr_scene} quiz={launch.documents?.assessment?.questions || []} lessonId={launch.id} students={[]} onQuizSubmitted={() => {}} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
